import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { gsap } from 'gsap';
import { getResults, exportReport, exportClientSummary, downloadReport } from '../api/client';
import FlagCard from '../components/FlagCard';

const prefersReduced = typeof window !== 'undefined'
  && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

const SOURCE_OPTIONS = [
  { value: '', label: 'All sources' },
  { value: 'agent1', label: 'AI Analysis' },
  { value: 'dre', label: 'Rules Engine' },
  { value: 'synthesis', label: 'Cross-reference' },
];

export default function ResultsDashboard() {
  const { id } = useParams();
  const [data, setData]           = useState(null);
  const [activeTab, setActiveTab] = useState('overzicht');
  const [flagFilter, setFlagFilter]     = useState('');
  const [sourceFilter, setSourceFilter] = useState('');
  const [exporting, setExporting]       = useState(false);
  const [exportFilename, setExportFilename] = useState('');
  const [included, setIncluded]   = useState({});   // finding id -> bool (red/amber only)
  const [lang, setLang] = useState('en');           // shared output language
  const [useSelection, setUseSelection] = useState(false);
  const root = useRef(null);
  const didReveal = useRef(false);

  const LANGS = [
    { v: 'en', label: 'English' }, { v: 'nl', label: 'Nederlands' },
    { v: 'de', label: 'Deutsch' }, { v: 'fr', label: 'Français' }, { v: 'es', label: 'Español' },
  ];

  useEffect(() => { getResults(id).then(setData); }, [id]);

  /* Seed the selection map from saved include_in_report once data arrives */
  useEffect(() => {
    if (!data) return;
    const m = {};
    data.findings.forEach(f => {
      if (f.flag_type === 'red' || f.flag_type === 'amber') m[f.id] = f.include_in_report !== 0;
    });
    setIncluded(m);
  }, [data]);

  const onToggleInclude = (fid, _flag, val) =>
    setIncluded(prev => ({ ...prev, [fid]: val }));

  // Re-file a finding live after a reviewer colour override so it lands in the
  // correct tab/count immediately (the backend returns the effective flag_type).
  const onFindingUpdate = (updated) =>
    setData(prev => prev
      ? { ...prev, findings: prev.findings.map(f => (f.id === updated.id ? { ...f, ...updated } : f)) }
      : prev);

  /* Header reveal + stat counters once data is in */
  useLayoutEffect(() => {
    if (!data) return;
    const ctx = gsap.context(() => {
      if (!prefersReduced && !didReveal.current) {
        didReveal.current = true;   // reveal once, not on every live update
        gsap.fromTo('.line-mask > span',
          { yPercent: 112 },
          { yPercent: 0, duration: 0.85, ease: 'power3.out', stagger: 0.1, delay: 0.05 },
        );
      }
      gsap.utils.toArray('.res-stat').forEach((el, i) => {
        const target = parseInt(el.dataset.target || '0', 10);
        if (prefersReduced) { el.textContent = target; return; }
        const obj = { v: 0 };
        gsap.to(obj, {
          v: target, duration: 1.2, ease: 'power2.out', delay: 0.25 + i * 0.08,
          snap: { v: 1 },
          onUpdate: () => { el.textContent = Math.floor(obj.v); },
        });
      });
    }, root);
    return () => ctx.revert();
  }, [data]);

  if (!data) return (
    <div className="page" style={{ textAlign: 'center', paddingTop: 80 }}>
      <p className="text-muted">Loading results...</p>
    </div>
  );

  const { review, findings, completeness, audit_trail, stats, run } = data;

  const filteredFindings = findings.filter(f => {
    if (flagFilter && f.flag_type !== flagFilter) return false;
    if (sourceFilter && f.source !== sourceFilter) return false;
    return true;
  });

  // Live counts off the current findings, so the top numbers update the instant
  // a reviewer changes a finding's colour (no page refresh needed).
  const liveRed   = findings.filter(f => f.flag_type === 'red').length;
  const liveAmber = findings.filter(f => f.flag_type === 'amber').length;
  const liveGreen = findings.filter(f => f.flag_type === 'green').length;

  async function handleExport(mode = 'standard') {
    setExporting(true);
    setExportFilename('');
    try {
      const result = await exportReport(id, mode, lang);
      setExportFilename(result.filename);
    } catch {
      alert('Report generation failed — check the backend is running and try again.');
    } finally {
      setExporting(false);
    }
  }

  async function handleClientSummary() {
    setExporting(true);
    setExportFilename('');
    try {
      const result = await exportClientSummary(id, lang, useSelection ? 'custom' : 'standard');
      setExportFilename(result.filename);
    } catch {
      alert('Client summary generation failed — check the backend is running and try again.');
    } finally {
      setExporting(false);
    }
  }

  async function handleDownload() {
    try {
      const blob = await downloadReport(id, exportFilename);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = exportFilename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch {
      alert('Download failed — try again.');
    }
  }

  const topRed = findings.filter(f => f.flag_type === 'red').slice(0, 3);

  // A missing/insufficient-document red reflects an incomplete dossier, not a
  // dangerous property — keep paperwork gaps from reading as "high risk". Mirrors
  // the report's _is_completeness logic so the platform and PDF agree.
  const isCompleteness = (f) => {
    const cat = (f.category || '').toLowerCase();
    const title = (f.title || '').toLowerCase();
    return cat.includes('volledigheid') || cat.includes('completeness')
      || ['ontbreek', 'onvoldoende', 'missing', 'insufficient'].some(k => title.includes(k));
  };
  const genuineRed = findings.filter(f => f.flag_type === 'red' && !isCompleteness(f)).length;

  const verdict = genuineRed > 0
    ? { label: 'Points to address', cls: 'badge-red', statement: 'review the items below with the seller.' }
    : (stats.total_red > 0 || stats.total_amber > 0)
      ? { label: 'A few points to review', cls: 'badge-amber', statement: 'a few items to clarify or complete.' }
      : { label: 'Looks healthy', cls: 'badge-green', statement: 'no material issues found.' };

  return (
    <div className="page theme-cream" ref={root}>
      {/* Header */}
      <div className="page-header" style={{ marginBottom: 40 }}>
        <div style={{ marginBottom: 18 }}>
          <Link to={`/reviews/${id}`} style={{ fontSize: 13, color: 'var(--text-label)' }}>← Back to upload</Link>
        </div>
        <div className="eyebrow" style={{ marginBottom: 16 }}>
          Analysis Report — Case #{String(id).padStart(2, '0')}
        </div>
        <div className="page-header-row" style={{ alignItems: 'flex-end' }}>
          <div style={{ minWidth: 0 }}>
            <h1 className="display-lg" style={{ marginBottom: 14 }}>
              <span className="line-mask"><span>{review.property_address}</span></span>
              <span className="line-mask"><span style={{
                fontSize: '0.62em', color: 'var(--text-secondary)',
              }}><em className="display-accent">{liveRed} red, {liveAmber} amber — {verdict.statement}</em></span></span>
            </h1>
            <p className="text-muted" style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
              <span className={`badge ${verdict.cls}`}>{verdict.label}</span>
              <span>
                Analysis complete · {new Date(run?.completed_at || review.created_at).toLocaleDateString('en-GB')}
                {run?.api_cost_usd > 0 && ` · API cost: $${run.api_cost_usd.toFixed(3)}`}
              </span>
            </p>
          </div>
          <button className="btn btn-primary btn-dot" onClick={() => setActiveTab('exporteren')}>
            Download Report
          </button>
        </div>
      </div>

      {/* Stats — big numerals */}
      <div className="numerals-row" style={{ marginBottom: 44 }}>
        {[
          { cls: 'red',   value: liveRed,               label: 'Red flags' },
          { cls: 'amber', value: liveAmber,             label: 'Amber flags' },
          { cls: 'green', value: liveGreen,             label: 'Clear' },
          { cls: stats.missing_docs > 0 ? 'red' : '', value: stats.missing_docs, label: 'Missing docs' },
          { cls: '',      value: stats.total_documents, label: 'Documents' },
        ].map((s, i) => (
          <div className={`numeral ${s.cls}`} key={i}>
            <div className="value res-stat" data-target={s.value}>0</div>
            <div className="label">{s.label}</div>
          </div>
        ))}
      </div>

      {/* Tabs */}
      <div className="tabs--pills">
        {[
          { key: 'overzicht',   label: 'Overview' },
          { key: 'bevindingen', label: `Findings (${findings.length})` },
          { key: 'volledigheid',label: `Completeness (${stats.missing_docs} missing)` },
          { key: 'auditspoor',  label: 'Audit trail' },
          { key: 'exporteren',  label: 'Export' },
        ].map(t => (
          <button
            key={t.key}
            className={`tab-pill ${activeTab === t.key ? 'active' : ''}`}
            onClick={() => setActiveTab(t.key)}
          >
            {t.label}
          </button>
        ))}
      </div>

      <AnimatePresence mode="wait">
        <motion.div
          key={activeTab}
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -8 }}
          transition={{ duration: 0.2 }}
        >

          {/* Overview */}
          {activeTab === 'overzicht' && (
            <div>
              {stats.missing_docs > 0 && (
                <div style={{
                  background: 'var(--card)',
                  borderRadius: 16, borderLeft: '4px solid var(--red)',
                  boxShadow: 'var(--shadow)',
                  padding: '18px 24px', marginBottom: 20,
                  display: 'flex', alignItems: 'center', gap: 16,
                }}>
                  <div style={{ color: 'var(--red)', fontSize: 20, flexShrink: 0 }}>⚠</div>
                  <div>
                    <strong style={{ color: 'var(--text)', fontSize: '0.95rem' }}>
                      {stats.missing_docs} document{stats.missing_docs !== 1 ? 's' : ''} missing from this package
                    </strong>
                    <p style={{ fontSize: 13, color: 'var(--text-label)', marginTop: 3 }}>
                      See the Completeness tab for the full list.
                    </p>
                  </div>
                </div>
              )}

              {topRed.length > 0 && (
                <div>
                  <h3 style={{ fontSize: '1rem', fontWeight: 600, marginBottom: 16, color: 'var(--text)' }}>
                    Top red findings
                  </h3>
                  {topRed.map(f => <FlagCard key={f.id} finding={f} onToggleInclude={onToggleInclude} onUpdate={onFindingUpdate} />)}
                  {findings.filter(f => f.flag_type === 'red').length > 3 && (
                    <button
                      className="btn btn-secondary btn-sm"
                      style={{ marginTop: 4 }}
                      onClick={() => { setActiveTab('bevindingen'); setFlagFilter('red'); }}
                    >
                      View all red findings →
                    </button>
                  )}
                </div>
              )}

              {topRed.length === 0 && (
                <div className="card" style={{ textAlign: 'center', padding: '48px 32px' }}>
                  <div style={{ fontSize: 40, marginBottom: 12 }}>✓</div>
                  <h3 style={{ color: 'var(--green)', fontWeight: 600, marginBottom: 6 }}>No red findings</h3>
                  <p className="text-muted text-sm">No high-risk issues were detected in this package.</p>
                </div>
              )}
            </div>
          )}

          {/* Findings */}
          {activeTab === 'bevindingen' && (
            <div>
              <div style={{ display: 'flex', gap: 8, marginBottom: 24, flexWrap: 'wrap', alignItems: 'center' }}>
                {['', 'red', 'amber', 'green'].map(v => (
                  <button
                    key={v}
                    className={`btn btn-sm ${flagFilter === v ? 'btn-primary' : 'btn-secondary'}`}
                    onClick={() => setFlagFilter(v)}
                  >
                    {v === ''      ? 'All' :
                     v === 'red'   ? '● Red' :
                     v === 'amber' ? '● Amber' : '● Clear'}
                    {' '}({findings.filter(f => v === '' || f.flag_type === v).length})
                  </button>
                ))}
                <select
                  className="form-control"
                  style={{ width: 'auto', fontSize: 13, borderRadius: 999, padding: '7px 14px' }}
                  value={sourceFilter}
                  onChange={e => setSourceFilter(e.target.value)}
                >
                  {SOURCE_OPTIONS.map(o => (
                    <option key={o.value} value={o.value}>{o.label}</option>
                  ))}
                </select>
              </div>

              {filteredFindings.length === 0 && (
                <div className="empty-state">
                  <p>No findings match this filter.</p>
                </div>
              )}

              {filteredFindings.map(f => <FlagCard key={f.id} finding={f} onToggleInclude={onToggleInclude} onUpdate={onFindingUpdate} />)}
            </div>
          )}

          {/* Completeness */}
          {activeTab === 'volledigheid' && (
            <div className="card">
              <h3 style={{ fontSize: '1rem', fontWeight: 600, marginBottom: 20 }}>Completeness check</h3>
              <div className="table-wrapper">
                <table>
                  <thead>
                    <tr>
                      <th>Document</th>
                      <th>Importance</th>
                      <th>Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {completeness.map((item, i) => {
                      const tierLabel = { M: 'Mandatory', I: 'Important', O: 'Optional' }[item.tier] || 'Mandatory';
                      let badge;
                      if (item.found) {
                        badge = <span className="badge badge-green">✓ Present</span>;
                      } else if (item.tier === 'I') {
                        badge = <span className="badge badge-amber">⚠ Recommended</span>;
                      } else if (item.tier === 'O') {
                        badge = <span className="badge" style={{ background: '#F3F4F6', color: '#6B7280' }}>○ Not provided</span>;
                      } else {
                        badge = <span className="badge badge-red">✗ Missing</span>;
                      }
                      return (
                        <tr key={i}>
                          <td style={{ fontWeight: 500, color: 'var(--text)' }}>{item.doc_label || item.doc_type}</td>
                          <td style={{ color: 'var(--text-label)', fontSize: 13 }}>{tierLabel}</td>
                          <td>{badge}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Audit trail */}
          {activeTab === 'auditspoor' && (
            <div className="card">
              <h3 style={{ fontSize: '1rem', fontWeight: 600, marginBottom: 20 }}>Audit trail</h3>
              {audit_trail.length === 0 && <p className="text-muted">No audit data available.</p>}
              <div className="table-wrapper">
                <table>
                  <thead>
                    <tr>
                      <th>Time</th>
                      <th>Event</th>
                      <th>Document</th>
                      <th>Summary</th>
                      <th>Tokens</th>
                    </tr>
                  </thead>
                  <tbody>
                    {audit_trail.map((entry, i) => (
                      <tr key={i}>
                        <td className="text-sm text-muted">
                          {entry.created_at ? new Date(entry.created_at).toLocaleTimeString('en-GB') : ''}
                        </td>
                        <td>
                          <span className="badge badge-gray" style={{ fontSize: 11 }}>
                            {entry.event_type}
                          </span>
                        </td>
                        <td className="text-sm">{entry.doc_type || '—'}</td>
                        <td className="text-sm" style={{ color: 'var(--text-secondary)' }}>
                          {(entry.result_summary || '').substring(0, 120)}
                        </td>
                        <td className="text-sm text-muted">{entry.tokens_used || 0}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Export */}
          {activeTab === 'exporteren' && (() => {
            const selRed   = (data.findings || []).filter(f => f.flag_type === 'red'   && included[f.id]).length;
            const selAmber = (data.findings || []).filter(f => f.flag_type === 'amber' && included[f.id]).length;
            return (
            <div style={{ display: 'grid', gap: 20, maxWidth: 560 }}>

              {/* Output language (applies to all exports below) */}
              <div className="card" style={{ display: 'flex', alignItems: 'center', gap: 14, flexWrap: 'wrap' }}>
                <strong style={{ fontSize: 14 }}>Output language</strong>
                <select className="form-control" value={lang} onChange={e => setLang(e.target.value)}
                        style={{ width: 'auto', fontSize: 13, borderRadius: 8, padding: '7px 12px' }}>
                  {LANGS.map(l => <option key={l.v} value={l.v}>{l.label}</option>)}
                </select>
                <span className="text-muted text-sm">findings are translated automatically</span>
              </div>

              {/* Custom (curated) report */}
              <div className="card">
                <h3 style={{ fontSize: '1rem', fontWeight: 600, marginBottom: 6 }}>Custom report</h3>
                <p className="text-muted text-sm" style={{ marginBottom: 16 }}>
                  Tick “In report” on the findings you want to include (in the Findings tab), then generate a
                  tailored report — the executive summary adapts to your selection.
                </p>
                <div style={{
                  display: 'flex', alignItems: 'center', gap: 14, marginBottom: 18,
                  padding: '12px 16px', background: 'rgba(19,19,19,0.03)', borderRadius: 12,
                }}>
                  <span style={{ fontSize: 22, fontWeight: 700, color: 'var(--red)' }}>{selRed}</span>
                  <span className="text-sm text-muted">red ·</span>
                  <span style={{ fontSize: 22, fontWeight: 700, color: 'var(--amber)' }}>{selAmber}</span>
                  <span className="text-sm text-muted">amber selected</span>
                </div>
                <button className="btn btn-primary" onClick={() => handleExport('custom')} disabled={exporting}>
                  {exporting ? 'Generating…' : 'Generate custom report'}
                </button>
              </div>

              {/* Standard full report */}
              <div className="card">
                <h3 style={{ fontSize: '1rem', fontWeight: 600, marginBottom: 6 }}>Standard full report</h3>
                <p className="text-muted text-sm" style={{ marginBottom: 16 }}>
                  The complete branded report with all current findings — no curation needed.
                </p>
                <button className="btn btn-secondary" onClick={() => handleExport('standard')} disabled={exporting}>
                  {exporting ? 'Generating…' : 'Generate full report'}
                </button>
              </div>

              {/* Client summary */}
              <div className="card">
                <h3 style={{ fontSize: '1rem', fontWeight: 600, marginBottom: 6 }}>Client summary</h3>
                <p className="text-muted text-sm" style={{ marginBottom: 16 }}>
                  A short, plain-language 3–4 page summary to share directly with your buyer.
                </p>
                <div style={{ display: 'flex', gap: 10, alignItems: 'center', marginBottom: 16, flexWrap: 'wrap' }}>
                  <label style={{ display: 'inline-flex', alignItems: 'center', gap: 7, fontSize: 13, color: 'var(--text-label)', cursor: 'pointer' }}>
                    <input type="checkbox" checked={useSelection} onChange={e => setUseSelection(e.target.checked)} />
                    From my selection
                  </label>
                </div>
                <button className="btn btn-secondary" onClick={handleClientSummary} disabled={exporting}>
                  {exporting ? 'Generating…' : 'Generate client summary'}
                </button>
              </div>

              {/* Download result */}
              {exportFilename && (
                <div className="card" style={{ borderLeft: '4px solid var(--green)' }}>
                  <div style={{ fontSize: 14, color: 'var(--green)', fontWeight: 500, marginBottom: 12 }}>
                    ✓ Ready: {exportFilename}
                  </div>
                  <button className="btn btn-primary" onClick={handleDownload}>↓ Download PDF</button>
                </div>
              )}
            </div>
            );
          })()}

        </motion.div>
      </AnimatePresence>
    </div>
  );
}
